import { Add, Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { OrderStatus } from 'enums';
import { TranslationNamespace } from 'i18n';
import { StatusMappingConfig } from 'types';

type Props = {
  value: StatusMappingConfig | undefined;
  onChange: (value: StatusMappingConfig) => void;
  disabled?: boolean;
};

type StatusRow = {
  providerStatus: string;
  internalStatus: string;
};

const INTERNAL_STATUSES = [
  OrderStatus.New,
  OrderStatus.Requisites,
  OrderStatus.CustomerConfirm,
  OrderStatus.TraderConfirm,
  OrderStatus.Completed,
  OrderStatus.HoldCompleted,
  OrderStatus.Cancelled,
  OrderStatus.Error,
  OrderStatus.Dispute,
];

const KEYS = {
  field_path: 'pages.dynamic_adapter_configs.dialog.status_mappings.field_path',
  field_path_help:
    'pages.dynamic_adapter_configs.dialog.status_mappings.field_path_help',
  sub_status_field:
    'pages.dynamic_adapter_configs.dialog.status_mappings.sub_status_field',
  default_status:
    'pages.dynamic_adapter_configs.dialog.status_mappings.default_status',
  primary_mappings:
    'pages.dynamic_adapter_configs.dialog.status_mappings.primary_mappings',
  provider_status:
    'pages.dynamic_adapter_configs.dialog.status_mappings.provider_status',
  internal_status:
    'pages.dynamic_adapter_configs.dialog.status_mappings.internal_status',
  actions: 'pages.dynamic_adapter_configs.dialog.mappings.actions',
  add_mapping: 'pages.dynamic_adapter_configs.dialog.mappings.add_mapping',
  no_mappings: 'pages.dynamic_adapter_configs.dialog.mappings.no_mappings',
} as const;

const getDefaultConfig = (): StatusMappingConfig => ({
  field: '$.status',
  primary: {},
  default: OrderStatus.New,
});

export const StatusMappingsEditor: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { t: tAdmin } = useTranslation(TranslationNamespace.Admin);
  const t = tAdmin as unknown as (key: string) => string;

  // Use local state to track rows, including incomplete ones
  const [localRows, setLocalRows] = useState<StatusRow[]>([]);
  // Track whether the last change was local to avoid circular useEffect sync
  const isLocalChange = useRef(false);

  // Derive config from value or create default
  const config = useMemo(() => value || getDefaultConfig(), [value]);

  // Sync rows from config when value changes externally
  useEffect(() => {
    if (isLocalChange.current) {
      isLocalChange.current = false;
      return;
    }
    if (config.primary) {
      const newRows = Object.entries(config.primary).map(
        ([providerStatus, internalStatus]) => ({
          providerStatus,
          internalStatus,
        }),
      );
      setLocalRows(newRows);
    } else {
      setLocalRows([]);
    }
  }, [config.primary]);

  const handleFieldChange = useCallback(
    (field: 'field' | 'subStatusField' | 'default', newValue: string) => {
      // When value is undefined, initialize with default config first
      const baseConfig = value || getDefaultConfig();
      isLocalChange.current = true;
      onChange({ ...baseConfig, [field]: newValue });
    },
    [value, onChange],
  );

  const propagateToParent = useCallback(
    (newRows: StatusRow[]) => {
      const primary: Record<string, string> = {};
      newRows.forEach((row) => {
        if (row.providerStatus) {
          primary[row.providerStatus] = row.internalStatus || OrderStatus.New;
        }
      });
      // When value is undefined, initialize with default config first
      const baseConfig = value || getDefaultConfig();
      isLocalChange.current = true;
      onChange({ ...baseConfig, primary });
    },
    [value, onChange],
  );

  const handleAddRow = useCallback(() => {
    const newRows = [
      ...localRows,
      { providerStatus: '', internalStatus: OrderStatus.New },
    ];
    setLocalRows(newRows);
    // Propagate to parent to ensure statusMappings is initialized
    propagateToParent(newRows);
  }, [localRows, propagateToParent]);

  const handleRemoveRow = useCallback(
    (index: number) => {
      const newRows = localRows.filter((_, i) => i !== index);
      setLocalRows(newRows);
      propagateToParent(newRows);
    },
    [localRows, propagateToParent],
  );

  const handleChangeRow = useCallback(
    (index: number, field: keyof StatusRow, newValue: string) => {
      const newRows = localRows.map((row, i) =>
        i === index ? { ...row, [field]: newValue } : row,
      );
      setLocalRows(newRows);
      propagateToParent(newRows);
    },
    [localRows, propagateToParent],
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={3}>
          <TextField
            fullWidth
            size="small"
            label={t(KEYS.field_path)}
            value={config.field || ''}
            onChange={(e) => handleFieldChange('field', e.target.value)}
            disabled={disabled}
            placeholder="$.response.status"
            helperText={t(KEYS.field_path_help)}
          />

          <TextField
            fullWidth
            size="small"
            label={t(KEYS.sub_status_field)}
            value={config.subStatusField || ''}
            onChange={(e) =>
              handleFieldChange('subStatusField', e.target.value)
            }
            disabled={disabled}
            placeholder="$.response.subStatus"
          />

          <FormControl fullWidth size="small">
            <InputLabel>{t(KEYS.default_status)}</InputLabel>
            <Select
              value={config.default || OrderStatus.New}
              label={t(KEYS.default_status)}
              onChange={(e) => handleFieldChange('default', e.target.value)}
              disabled={disabled}
            >
              {INTERNAL_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
              {t(KEYS.primary_mappings)}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t(KEYS.provider_status)}</TableCell>
                  <TableCell>{t(KEYS.internal_status)}</TableCell>
                  <TableCell width={80} align="center">
                    {t(KEYS.actions)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t(KEYS.no_mappings)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  localRows.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.providerStatus}
                          onChange={(e) =>
                            handleChangeRow(
                              index,
                              'providerStatus',
                              e.target.value,
                            )
                          }
                          disabled={disabled}
                          placeholder="success"
                        />
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={row.internalStatus}
                            onChange={(e) =>
                              handleChangeRow(
                                index,
                                'internalStatus',
                                e.target.value,
                              )
                            }
                            disabled={disabled}
                          >
                            {INTERNAL_STATUSES.map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveRow(index)}
                          disabled={disabled}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddRow}
                disabled={disabled}
              >
                {t(KEYS.add_mapping)}
              </Button>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
